import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import { AppModule } from '@/store/Auth/AppModule';
import ThirdPartyAccount from '@/models/Accounts/ThirdPartyAccounts/ThirdPartyAccount';
import ThirdPartyTransaction from '@/models/Accounts/ThirdPartyAccounts/ThirdPartyTransaction';
import InstitutionAccount from '@/models/Accounts/InstitutionAccounts/InstitutionAccount';
import GetTransactionsResponse from '@/models/Accounts/Shared/Transactions/GetTransactionsResponse';
import Transaction from '@/models/Accounts/Shared/Transactions/Transaction';
import TransactionsOrderOptions from "@/models/Accounts/Shared/Transactions/TransactionsOrderOptions";
import store from '@/store';
import Payee from '@/models/Accounts/Shared/Payees/Payee';
import PaymentOrder from '@/models/Accounts/Shared/PaymentOrders/PaymentOrder';
import CreatePayeeRequest from '@/models/Accounts/Shared/Payees/CreatePayeeRequest';
import CreatePaymentOrderRequest from '@/models/Accounts/Shared/PaymentOrders/CreatePaymentOrderRequest';
import CancelRejectPaymentOrderRequest from '@/models/Accounts/Shared/PaymentOrders/CancelRejectPaymentOrderRequest';
import Payment from '@/models/Accounts/Shared/Payments/Payment';
import PaymentState from '@/models/Accounts/Shared/Payments/PaymentState';
import AccountType from '@/models/Shared/AccountType';
import UpdateInstitutionAccountSettingsRequest from '@/models/Accounts/InstitutionAccounts/Settings/UpdateInstitutionAccountSettingsRequest';
import PermissionUtils from '@/lib/permissionUtils';
import ValidationUtils from '@/lib/validationUtils';

export interface IInstitutionAccountState {
    loading: boolean;
    loadingDialog: boolean;
    loadingThirdPartyAccounts: boolean;
    loadingThirdPartyAccount: boolean;
    loadingThirdPartyTransactions: boolean;
    loadingInstitutionalAccounts: boolean;
    loadingInstitutionalAccount: boolean;
    loadingInstitutionalTransactions: boolean;
    loadingPaymentOrders: boolean;
    loadingPayees: boolean;
    loadingPaymentsForPaymentOrder: boolean;
    loadingPayments: boolean;
    thirdPartyAccount: ThirdPartyAccount | null;
    totalThirdPartyAccountsPages: number;
    thirdPartyAccounts: ThirdPartyAccount[];
    thirdPartyTransactions: ThirdPartyTransaction[];
    totalThirdPartyTransactionsPages: number;
    thirdPartyTransactionsOrder: TransactionsOrderOptions;
    institutionalAccount: InstitutionAccount | null;
    totalInstitutionalAccountsPages: number;
    institutionalAccounts: InstitutionAccount[];
    institutionalTransactions: Transaction[];
    totalInstitutionalAccountTransactionsPages: number;
    institutionalTransactionsOrder: TransactionsOrderOptions;
    institutionalTransactionsSearch: string;
    payees: Payee[];
    payeesForDialog: Payee[];
    totalPayeesPages: number;
    currentPayee: Payee;
    paymentOrders: PaymentOrder[];
    totalPaymentOrdersPages: number;
    currentPaymentOrder: PaymentOrder;
    paymentsForPaymentOrder: Payment[];
    payments: Payment[];
    totalPaymentsPages: number;
    currentPayment: Payment;
    filterByPaymentState: PaymentState;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'InstitutionAccountModule', namespaced: true })
class InstitutionAccountStore extends VuexModule implements IInstitutionAccountState {

    public loading: boolean = true;
    public loadingDialog: boolean = false;
    public loadingThirdPartyAccounts: boolean = true;
    public loadingThirdPartyAccount: boolean = true;
    public loadingThirdPartyTransactions: boolean = true;
    public loadingInstitutionalAccounts: boolean = true;
    public loadingInstitutionalAccount: boolean = true;
    public loadingInstitutionalTransactions: boolean = true;
    public loadingPaymentOrders: boolean = true;
    public loadingPayees: boolean = true;
    public loadingPaymentsForPaymentOrder: boolean = true;
    public loadingPayments: boolean = true;
    public thirdPartyAccount: ThirdPartyAccount = new ThirdPartyAccount();
    public totalThirdPartyAccountsPages: number = 1;
    public thirdPartyAccounts: ThirdPartyAccount[] = [];
    public thirdPartyTransactions: ThirdPartyTransaction[] = [];
    public totalThirdPartyTransactionsPages: number = 1;
    public thirdPartyTransactionsOrder: TransactionsOrderOptions = TransactionsOrderOptions.DisplayDate;
    public institutionalAccount: InstitutionAccount = new InstitutionAccount();
    public totalInstitutionalAccountsPages: number = 1;
    public institutionalAccounts: InstitutionAccount[] = [];
    public institutionalTransactions: Transaction[] = [];
    public totalInstitutionalAccountTransactionsPages: number = 1;
    public institutionalTransactionsOrder: TransactionsOrderOptions = TransactionsOrderOptions.DisplayDate;
    public institutionalTransactionsSearch: string = '';
    public payees: Payee[] = [];
    public payeesForDialog: Payee[] = [];
    public totalPayeesPages: number = 1;
    public currentPayee: Payee = new Payee();
    public paymentOrders: PaymentOrder[] = [];
    public totalPaymentOrdersPages: number = 1;
    public currentPaymentOrder: PaymentOrder = new PaymentOrder();
    public paymentsForPaymentOrder: Payment[] = [];
    public payments: Payment[] = [];
    public totalPaymentsPages: number = 1;
    public currentPayment: Payment = new Payment();
    public filterByPaymentState: PaymentState = PaymentState.Completed;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getThirdPartyAccounts() {

        this.setLoadingThirdPartyAccounts(true);

        await ajax.get(`/portal-backend/fi-api/internal/third-party-accounts`)
        .then(response => {
            this.context.commit("setThirdPartyAccounts", response.data.accounts);
            // There are no "records" in the response in this case, so we have to calculate the length of results manually
            this.context.commit("setThirdPartyAccountsTotalPages", response.data.accounts.length);
            this.setLoadingThirdPartyAccounts(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingThirdPartyAccounts(false);
        });
    }

    @Action
    async getThirdPartyAccount(id: string) {

        this.setLoadingThirdPartyAccount(true);

        await ajax.get(`/portal-backend/fi-api/internal/third-party-accounts/${id}`)
        .then(response => {
            this.context.commit("setThirdPartyAccount", response.data.account);
            this.setLoadingThirdPartyAccount(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingThirdPartyAccount(false);
        });
    }

    @Action
    async getThirdPartyTransactions(id: string) {

        this.setLoadingThirdPartyTransactions(true);

        const orderParameter = this.thirdPartyTransactionsOrder ? `&orderBy=${this.thirdPartyTransactionsOrder}` : '';

        await ajax.get(`/portal-backend/fi-api/internal/third-party-accounts/${id}/transactions?order=DESC${orderParameter}`)
        .then(response => {
            if (response.data.transactions === null){
                response.data = [];
            }
            this.context.commit("setThirdPartyTransactions", response.data.transactions);
            this.context.commit("setThirdPartyTransactionsTotalPages", response.data.records.totalRecords);
            this.setLoadingThirdPartyTransactions(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingThirdPartyTransactions(false);
        });
    }


    @Action
    async updateThirdPartyTransactionsOrder(order: TransactionsOrderOptions) {
        this.context.commit("setThirdPartyTransactionsOrder", order);
    }

    @Action
    async getInstitutionalAccounts() {

        this.setLoadingInstitutionalAccounts(true);

        await ajax.get(`/portal-backend/fi-api/internal/institutional-accounts`)
        .then(response => {
            this.context.commit("setInstitutionalAccounts", response.data.results);
            this.context.commit("setInstitutionalAccountsTotalPages", response.data.records.totalRecords);
            this.setLoadingInstitutionalAccounts(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingInstitutionalAccounts(false);
        });
    }

    @Action
    async getInstitutionalAccount(id: string) {

        this.setLoadingInstitutionalAccount(true);

        await ajax.get(`/portal-backend/fi-api/internal/institutional-accounts/${id}/settings`)
        .then(response => {
            this.context.commit("setInstitutionalAccount", response.data);
            this.setLoadingInstitutionalAccount(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingInstitutionalAccount(false);
        });
    }

    @Action
    async editInstitutionalAccountSettings(request: UpdateInstitutionAccountSettingsRequest) {

        this.setLoading(true);

        await ajax.patch(`/portal-backend/fi-api/internal/institutional-accounts/${this.institutionalAccount.id}/settings`, request)
        .then(response => {
            this.context.commit("setInstitutionalAccount", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getInstitutionalTransactions(data: {id: string, page: number}) {

        this.setLoadingInstitutionalTransactions(true);

        const searchParameter = this.institutionalTransactionsSearch ? `&reference=${ValidationUtils.encodeRFC5987ValueChars(this.institutionalTransactionsSearch)}` : '';

        await ajax.get(`/portal-backend/fi-api/internal/institutional-accounts/${data.id}/transactions?order=DESC&pageSize=${this.pageSize}&pageNumber=${data.page}${searchParameter}`)
        .then(response => {
            if (response.data.transactions === null){
                response.data = [];
            }
            this.context.commit("setInstitutionalTransactions", response.data);
            this.context.commit("setInstitutionalTransactionsTotalPages", response.data.records.totalRecords);
            this.setLoadingInstitutionalTransactions(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingInstitutionalTransactions(false);
        });
    }

    @Action
    async updateInstitutionalTransactionsOrder(order: TransactionsOrderOptions) {
        this.context.commit("setInstitutionalTransactionsOrder", order);
    }

    @Action
    async updateInstitutionalTransactionsSearch(searchText: string) {
        this.context.commit("setInstitutionalTransactionsSearch", searchText);
    }

    @Action
    async createPayee(request: CreatePayeeRequest) {

        this.setLoadingDialog(true);
        request.accountType = AccountType.Institution;

        await ajax.post(`/portal-backend/fi-api/payees`, request)
        .then(response => {
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getPayees(data: { id: string, pageNumber: number }) {

        this.setLoadingPayees(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${data.id}/payees?pageSize=${this.pageSize}&pageNumber=${data.pageNumber}`)
        .then(response => {
            this.context.commit("setPayees", response.data.results);
            this.context.commit("setPayeesTotalPages", response.data.records.totalRecords);
            if (response.data.results.length > 0) {
                this.context.commit("setCurrentPayee", response.data.results[0]);
            }
            this.setLoadingPayees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayees(false);
        });
    }

    @Action
    async getPayeesForDialog(id: string) {

        // This action is specifically for the edit/create dialogs where we need to ignore the normal API pagination
        // logic in getPayees and instead get all payees so they can be listed in the drop down menu for dialogs
        this.setLoadingPayees(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${id}/payees`)
        .then(response => {
            this.context.commit("setPayeesForDialog", response.data.results);
            this.setLoadingPayees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayees(false);
        });
    }

    @Action
    async deletePayee() {

        this.setLoadingPayees(true);

        await ajax.delete(`/portal-backend/fi-api/payees/${this.currentPayee.id}`)
        .then(() => {
            this.setLoadingPayees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayees(false);
        });
    }

    @Action
    public async updateCurrentPayee(payee: Payee){
        this.context.commit("setCurrentPayee", payee);
    }

    @Action
    async createPaymentOrder(request: CreatePaymentOrderRequest) {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders`, request)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async approvePaymentOrder() {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders/${this.currentPaymentOrder.id}/approve`)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async cancelPaymentOrder(request: CancelRejectPaymentOrderRequest) {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders/${this.currentPaymentOrder.id}/cancel`, request)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async rejectPaymentOrder(request: CancelRejectPaymentOrderRequest) {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders/${this.currentPaymentOrder.id}/reject`, request)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getPaymentOrders(data: { id: string, pageNumber: number }) {

        this.setLoadingPaymentOrders(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${data.id}/payment-orders?pageSize=${this.pageSize}&pageNumber=${data.pageNumber}`)
        .then(response => {
            this.context.commit("setPaymentOrders", response.data.results);
            this.context.commit("setPaymentOrdersTotalPages", response.data.records.totalRecords);
            if (response.data.results.length > 0) {
                this.context.commit("setCurrentPaymentOrder", response.data.results[0]);
            }
            this.setLoadingPaymentOrders(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPaymentOrders(false);
        });
    }

    @Action
    public async updateCurrentPaymentOrder(paymentOrder: PaymentOrder){
        this.context.commit("setCurrentPaymentOrder", paymentOrder);
    }

    @Action
    async getPaymentsForPaymentOrder(id: string) {

        this.setLoadingPaymentsForPaymentOrder(true);

        await ajax.get(`/portal-backend/fi-api/payment-orders/${id}`)
        .then(response => {
            this.context.commit("setPaymentsForPaymentOrder", response.data.payments);
            this.setLoadingPaymentsForPaymentOrder(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPaymentsForPaymentOrder(false);
        });
    }

    @Action
    async getPayments(data: { id: string, pageNumber: number }) {

        this.setLoadingPayments(true);

        await ajax.get(`/portal-backend/fi-api/internal/institutional-accounts/${data.id}/payments?pageSize=${this.pageSize}&pageNumber=${data.pageNumber}&state=${this.filterByPaymentState}`)
        .then(response => {
            this.context.commit("setPayments", response.data.results);
            this.context.commit("setPaymentsTotalPages", response.data.records.totalRecords);
            if (response.data.results.length > 0) {
                this.context.commit("setCurrentPayment", response.data.results[0]);
            }
            this.setLoadingPayments(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayments(false);
        });
    }

    @Action
    public async updateCurrentPayment(payment: Payment){
        this.context.commit("setCurrentPayment", payment);
    }

    @Action
    public async clearPaymentsForPaymentOrder(){
        this.context.commit("setPaymentsForPaymentOrder", []);
    }

    @Action
    async updateFilterByPaymentState(val: PaymentState) {
        this.context.commit("setFilterByPaymentState", val);
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingDialog = false;
        this.loadingThirdPartyAccounts = true;
        this.loadingThirdPartyAccount = true;
        this.loadingThirdPartyTransactions = true;
        this.loadingInstitutionalAccounts = true;
        this.loadingInstitutionalAccount = true;
        this.loadingInstitutionalTransactions = true;
        this.loadingPaymentOrders = true;
        this.loadingPayees = true;
        this.loadingPaymentsForPaymentOrder = true;
        this.loadingPayments = true;
        this.thirdPartyAccounts = [];
        this.totalThirdPartyAccountsPages = 1;
        this.thirdPartyTransactionsOrder = TransactionsOrderOptions.DisplayDate;
        this.thirdPartyAccount = new ThirdPartyAccount();
        this.thirdPartyTransactions = [];
        this.totalThirdPartyTransactionsPages = 1;
        this.institutionalAccounts = [];
        this.totalInstitutionalAccountsPages = 1;
        this.institutionalAccount = new InstitutionAccount();
        this.institutionalTransactions = []
        this.totalInstitutionalAccountTransactionsPages = 1;
        this.institutionalTransactionsOrder = TransactionsOrderOptions.DisplayDate;
        this.institutionalTransactionsSearch = '';
        this.payees = [];
        this.payeesForDialog = [];
        this.totalPayeesPages = 1;
        this.currentPayee = new Payee();
        this.paymentOrders = [];
        this.totalPaymentOrdersPages = 1;
        this.currentPaymentOrder = new PaymentOrder();
        this.paymentsForPaymentOrder = [];
        this.payments = [];
        this.totalPaymentsPages = 1;
        this.currentPayment =  new Payment();
        this.filterByPaymentState = PaymentState.Completed;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setLoadingThirdPartyAccounts(loading: boolean) {
        this.loadingThirdPartyAccounts = loading;
    }

    @Mutation
    private async setLoadingThirdPartyAccount(loading: boolean) {
        this.loadingThirdPartyAccount = loading;
    }

    @Mutation
    private async setLoadingThirdPartyTransactions(loading: boolean) {
        this.loadingThirdPartyTransactions = loading;
    }

    @Mutation
    private async setLoadingInstitutionalAccounts(loading: boolean) {
        this.loadingInstitutionalAccounts = loading;
    }

    @Mutation
    private async setLoadingInstitutionalAccount(loading: boolean) {
        this.loadingInstitutionalAccount = loading;
    }

    @Mutation
    private async setLoadingInstitutionalTransactions(loading: boolean) {
        this.loadingInstitutionalTransactions = loading;
    }

    @Mutation
    private async setInstitutionalTransactionsOrder(order: TransactionsOrderOptions) {
        this.institutionalTransactionsOrder = order;
    }

    @Mutation
    private async setLoadingPaymentOrders(loading: boolean) {
        this.loadingPaymentOrders = loading;
    }

    @Mutation
    private async setLoadingPaymentsForPaymentOrder(loading: boolean) {
        this.loadingPaymentsForPaymentOrder = loading;
    }

    @Mutation
    private async setLoadingPayments(loading: boolean) {
        this.loadingPayments = loading;
    }

    @Mutation
    private async setLoadingPayees(loading: boolean) {
        this.loadingPayees = loading;
    }

    @Mutation
    private async setThirdPartyAccounts(response: ThirdPartyAccount[]) {
        if (response) {
            this.thirdPartyAccounts = response;
        }
    }

    @Mutation
    private async setThirdPartyAccountsTotalPages(total: number) {
        this.totalThirdPartyAccountsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setThirdPartyAccount(response: ThirdPartyAccount) {
        this.thirdPartyAccount = response;
    }

    @Mutation
    private async setThirdPartyTransactions(response: ThirdPartyTransaction[]) {
        if (response) {
            this.thirdPartyTransactions = response;
        }
    }

    @Mutation
    private async setThirdPartyTransactionsTotalPages(total: number) {
        this.totalThirdPartyTransactionsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setThirdPartyTransactionsOrder(order: TransactionsOrderOptions) {
        this.thirdPartyTransactionsOrder = order;
    }

    @Mutation
    private async setInstitutionalAccounts(response: InstitutionAccount[]) {
        if (response) {
            this.institutionalAccounts = response;
        }
    }

    @Mutation
    private async setInstitutionalAccountsTotalPages(total: number) {
        this.totalInstitutionalAccountsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setInstitutionalAccount(response: InstitutionAccount) {
        this.institutionalAccount = response;
    }

    @Mutation
    private async setInstitutionalTransactions(response: GetTransactionsResponse) {
        this.institutionalTransactions = response.transactions;
    }

    @Mutation
    private async setInstitutionalTransactionsTotalPages(total: number) {
        this.totalInstitutionalAccountTransactionsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setInstitutionalTransactionsSearch(text: string) {
        this.institutionalTransactionsSearch = text;
    }

    @Mutation
    private async setPayees(response: Payee[]) {
        if (response) {
            this.payees = response;
        }
    }

    @Mutation
    private async setPayeesForDialog(response: Payee[]) {
        if (response) {
            this.payeesForDialog = response;
        }
    }

    @Mutation
    private async setPayeesTotalPages(total: number) {
        this.totalPayeesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentPayee(payee: Payee) {
        this.currentPayee = payee;
    }

    @Mutation
    private async setPaymentOrders(response: PaymentOrder[]) {
        if (response) {
            this.paymentOrders = response;
        }
    }

    @Mutation
    private async setPaymentOrdersTotalPages(total: number) {
        this.totalPaymentOrdersPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentPaymentOrder(paymentOrder: PaymentOrder) {
        this.currentPaymentOrder = paymentOrder;
    }

    @Mutation
    private async setPaymentOrderFromUpdate(paymentOrder: PaymentOrder) {
        this.currentPaymentOrder = paymentOrder;
        const index = this.paymentOrders.findIndex((findPO: PaymentOrder) => paymentOrder.id === findPO.id);
        this.paymentOrders.splice(index, 1, paymentOrder);
    }

    @Mutation
    private async setPaymentsForPaymentOrder(response: Payment[]) {
        if (response) {
            this.paymentsForPaymentOrder = response;
        }
    }

    @Mutation
    private async setPayments(response: Payment[]) {
        if (response) {
            this.payments = response;
        }
    }

    @Mutation
    private async setPaymentsTotalPages(total: number) {
        this.totalPaymentsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentPayment(payment: Payment) {
        this.currentPayment = payment;
    }

    @Mutation
    private async setFilterByPaymentState(response: PaymentState) {
        this.filterByPaymentState = response;
    }

}

export const InstitutionAccountModule = getModule(InstitutionAccountStore);